import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { tasksAPI, projectsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, CheckCircle2, Clock, ListTodo, Trash2, Loader2, Edit, MoreVertical } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const STATUS_CONFIG = {
  todo: { label: 'To-Do', icon: ListTodo, color: 'text-[#78866B]', bg: 'bg-[#78866B]/10', badge: 'bg-[#78866B]/10 text-[#78866B]' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'text-[#C98E75]', bg: 'bg-[#C98E75]/10', badge: 'bg-[#C98E75]/10 text-[#C98E75]' },
  done: { label: 'Done', icon: CheckCircle2, color: 'text-[#57534E]', bg: 'bg-[#57534E]/10', badge: 'bg-[#57534E]/10 text-[#57534E]' },
};

export default function Tasks() {
  const { t } = useLanguage();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    title: '',
    description: '',
    status: 'todo'
  });

  const fetchData = async () => {
    try {
      const [tasksRes, projectsRes] = await Promise.all([
        tasksAPI.getAll(),
        projectsAPI.getAll()
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setFormData({ project_id: '', title: '', description: '', status: 'todo' });
    setEditingTask(null);
  };

  const openEdit = (task) => {
    setFormData({
      project_id: task.project_id,
      title: task.title,
      description: task.description || '',
      status: task.status
    });
    setEditingTask(task);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error('Title is required'); return; }
    if (!formData.project_id) { toast.error('Please select a project'); return; }

    setSaving(true);
    try {
      if (editingTask) {
        await tasksAPI.update(editingTask.id, formData);
        toast.success(t('taskUpdated'));
      } else {
        await tasksAPI.create(formData);
        toast.success(t('taskCreated'));
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await tasksAPI.update(task.id, { status: newStatus });
      toast.success(t('taskUpdated'));
      fetchData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await tasksAPI.delete(task.id);
      toast.success(t('taskDeleted'));
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const grouped = {
    todo: tasks.filter(t => t.status === 'todo'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    done: tasks.filter(t => t.status === 'done'),
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 w-48 bg-[#E7E5E4] rounded-lg animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <div key={i} className="h-64 bg-[#E7E5E4] rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-medium text-[#292524] tracking-tight">{t('tasks')}</h1>
          <p className="text-[#78716C] mt-1">{tasks.length} {t('totalTasksCount')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90 h-11 px-6" data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('newTask')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium">
                {editingTask ? t('editTask') : t('newTask')}
              </DialogTitle>
              <DialogDescription className="sr-only">Task form</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('taskTitle')} *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('taskTitle')}
                  className="h-11"
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('project')} *</Label>
                <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                  <SelectTrigger className="h-11" data-testid="task-project-select">
                    <SelectValue placeholder={t('selectProject')} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('status')}</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="h-11" data-testid="task-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo">To-Do</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('description')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('description')}
                  rows={3}
                  data-testid="task-description-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90" disabled={saving} data-testid="save-task-btn">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingTask ? t('update') : t('create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Task columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(STATUS_CONFIG).map(([statusKey, config]) => {
          const StatusIcon = config.icon;
          const statusTasks = grouped[statusKey];
          return (
            <Card key={statusKey} className="border-0 shadow-sm" data-testid={`task-column-${statusKey}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center`}>
                    <StatusIcon className={`w-4 h-4 ${config.color}`} />
                  </div>
                  <CardTitle className="text-base font-medium">{config.label}</CardTitle>
                  <Badge variant="outline" className="ml-auto rounded-full text-xs">{statusTasks.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {statusTasks.length === 0 ? (
                  <p className="text-sm text-[#A8A29E] text-center py-6">{t('noTasks')}</p>
                ) : (
                  statusTasks.map(task => (
                    <div key={task.id} className="p-3 rounded-xl bg-[#F5F5F4]/50 hover:bg-[#F5F5F4] transition-colors group" data-testid={`task-${task.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-[#292524] truncate">{task.title}</p>
                          <p className="text-xs text-[#78716C] mt-0.5 truncate">{task.project_name}{task.client_name ? ` — ${task.client_name}` : ''}</p>
                          {task.description && <p className="text-xs text-[#A8A29E] mt-1 line-clamp-2">{task.description}</p>}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(task)}>
                              <Edit className="w-4 h-4 mr-2" /> {t('edit')}
                            </DropdownMenuItem>
                            {statusKey !== 'todo' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(task, 'todo')}>
                                <ListTodo className="w-4 h-4 mr-2" /> To-Do
                              </DropdownMenuItem>
                            )}
                            {statusKey !== 'in_progress' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(task, 'in_progress')}>
                                <Clock className="w-4 h-4 mr-2" /> In Progress
                              </DropdownMenuItem>
                            )}
                            {statusKey !== 'done' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(task, 'done')}>
                                <CheckCircle2 className="w-4 h-4 mr-2" /> Done
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDelete(task)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> {t('delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
